import type { BookHeader, LogEntry } from "./boardbook-model";

/**
 * A single per-entry change received from a peer (one kernel row = one entry,
 * keyed by uuid = `LogEntry.id`). An `upsert` carries the whole entry; a
 * `delete` carries only the id + the delete's `updatedAt` (its LWW key).
 */
export type EntryOp = { kind: "upsert"; entry: LogEntry } | { kind: "delete"; id: string; updatedAt: number };

/**
 * Fold one peer entry-op into the local entry list by **per-entry LWW**.
 *
 * This replaces the whole-blob union merge: because each entry is its own kernel
 * row broadcast independently to every peer, there is no cross-entry merge and
 * no convergence re-push — the kernel already fans each row out. An op only wins
 * when its `updatedAt` is **strictly greater** than the local entry's, so a
 * device's own echoed write (equal ms) is a no-op and folding is idempotent.
 *
 * The kernel is the authority on a seal (non-empty `lockedAt`, issue #945). A
 * remote upsert that carries a `lockedAt` applies onto an unlocked local entry
 * whatever the stamps (a refused edit hands the device an older
 * but sealed row), and onto a locked local entry when it is strictly newer. An
 * unlocked remote upsert never overwrites a locked local entry. A locked local
 * entry still ignores a remote delete.
 *
 * Returns the (possibly new) list plus `changed`, so the caller skips a needless
 * persist when nothing moved.
 */
export function applyEntryOp(entries: LogEntry[], op: EntryOp): { entries: LogEntry[]; changed: boolean } {
  if (op.kind === "delete") {
    const idx = entries.findIndex((e) => e.id === op.id);
    // Unknown id: nothing to tombstone. Locked local: sealed, ignore. Stale delete (<=local): keep local.
    if (idx < 0 || entries[idx].lockedAt || op.updatedAt <= entries[idx].updatedAt) {
      return { entries, changed: false };
    }
    return { entries: entries.filter((_, i) => i !== idx), changed: true };
  }

  const idx = entries.findIndex((e) => e.id === op.entry.id);
  if (idx < 0) return { entries: [...entries, op.entry], changed: true };
  const local = entries[idx];
  const apply = op.entry.lockedAt
    ? !local.lockedAt || op.entry.updatedAt > local.updatedAt
    : !local.lockedAt && op.entry.updatedAt > local.updatedAt;
  if (!apply) return { entries, changed: false };
  const next = entries.slice();
  next[idx] = op.entry;
  return { entries: next, changed: true };
}

/**
 * Fold a remote book header into the local one by strict last-write-wins.
 * Remote wins only when `remote.updatedAt` is strictly greater than
 * `local.updatedAt`; equal (a device's own echo) or older is a no-op.
 */
export function applyHeaderOp(local: BookHeader, remote: BookHeader): { header: BookHeader; changed: boolean } {
  if (remote.updatedAt > local.updatedAt) return { header: remote, changed: true };
  return { header: local, changed: false };
}
